import { Box, useToast } from "@chakra-ui/react";
import { useState } from "react";
import type {
  CampaignIntent,
  CampaignIntentStatus,
  DisplayMarketingCampaign,
} from "common/interfaces/KYDMarketing";
import actions from "src/actions";
import { getIntentPerformanceCategory } from "./intentPerformance";
import CampaignIntentItem from "./CampaignIntentItem";

/** Do not show DISABLED (superseded) or IGNORED (user dismissed) intents. */
function isVisibleIntent(intent: CampaignIntent): boolean {
  const s = intent.status as CampaignIntentStatus | undefined;
  return s !== "DISABLED" && s !== "IGNORED";
}

/** Only SUGGESTED (or undefined) intents show the Apply button / can be applied. */
function isApplicable(intent: CampaignIntent): boolean {
  if (!intent.actionable) return false;
  const s = intent.status as CampaignIntentStatus | undefined;
  return s === "SUGGESTED" || s === undefined;
}

const SEVERITY_ORDER: Record<string, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

/** Performance tier for ordering: good (0) first, then bad (1), then neutral (2). */
function getPerformanceTier(intent: CampaignIntent): number {
  const cat = getIntentPerformanceCategory(intent);
  return cat === "positive" ? 0 : cat === "negative" ? 1 : 2;
}

/** Sort intents: good (e.g. increase budget) first, then bad, then neutral; within that by severity (error, warning, info). */
export function sortIntentsByImportance(
  intents: CampaignIntent[]
): CampaignIntent[] {
  return [...intents].sort((a, b) => {
    const ta = getPerformanceTier(a);
    const tb = getPerformanceTier(b);
    if (ta !== tb) return ta - tb;
    const sa = SEVERITY_ORDER[a.severity ?? "info"] ?? 2;
    const sb = SEVERITY_ORDER[b.severity ?? "info"] ?? 2;
    return sa - sb;
  });
}

export const MAX_DISPLAYED_INTENTS = 5;

interface CampaignIntentsBlockProps {
  campaignId: string;
  intents: CampaignIntent[] | undefined;
  campaign?: DisplayMarketingCampaign;
  onApplied?: () => void | Promise<void>;
  onApplyButtonHover?: (entityIds: string[]) => void;
  onApplyButtonLeave?: () => void;
  onDismiss?: (intent: CampaignIntent) => void;
  /** When set, clicking an insight scrolls the page to the corresponding ad set card. */
  onScrollToAdset?: (adsetId: string) => void;
}

const CampaignIntentsBlock: React.FC<CampaignIntentsBlockProps> = ({
  campaignId,
  intents,
  campaign,
  onApplied,
  onApplyButtonHover,
  onApplyButtonLeave,
  onDismiss,
  onScrollToAdset,
}) => {
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [ignoringId, setIgnoringId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const toast = useToast();

  const visibleIntents = (intents ?? []).filter(isVisibleIntent);
  const displayedIntents = visibleIntents.filter(
    (i) => !dismissedIds.has(i.id)
  );
  if (!displayedIntents.length) return null;

  const handleApply = async (e: React.MouseEvent, intent: CampaignIntent) => {
    e.stopPropagation();
    if (!intent.actionable || !isApplicable(intent)) return;
    setApplyingId(intent.id);
    try {
      await actions.ADMIN.batchApplyCampaignIntents([
        { campaign_id: campaignId, intent_ids: [intent.id] },
      ]);
      toast({
        title: "Intent applied",
        description: "Recommended action was applied successfully.",
        status: "success",
        isClosable: true,
      });
      await onApplied?.();
    } catch (err: any) {
      toast({
        title: "Failed to apply",
        description: err?.message ?? "Could not apply the recommended action.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setApplyingId(null);
    }
  };

  const getIntentLabel = (intent: CampaignIntent): string => {
    const short =
      intent.explanation?.short_summary ?? intent.explanation?.summary;
    if (short) return short.length > 80 ? `${short.slice(0, 77)}…` : short;
    const firstLabel = intent.actions?.[0]?.label;
    if (firstLabel) return firstLabel;
    if (intent.reason)
      return intent.reason.length > 80
        ? `${intent.reason.slice(0, 77)}…`
        : intent.reason;
    return "Insight";
  };

  const handleDismiss = async (e: React.MouseEvent, intent: CampaignIntent) => {
    e.stopPropagation();
    setIgnoringId(intent.id);
    onDismiss?.(intent);
    try {
      await actions.ADMIN.ignoreCampaignIntent(campaignId, [intent]);
      setDismissedIds((prev) => new Set(prev).add(intent.id));
      toast({
        title: "Insight dismissed",
        description: getIntentLabel(intent),
        status: "success",
        isClosable: true,
      });
      await onApplied?.();
    } catch (err: any) {
      toast({
        title: "Could not ignore",
        description: err?.message ?? "Intent could not be saved as ignored.",
        status: "warning",
        isClosable: true,
      });
    } finally {
      setIgnoringId(null);
    }
  };

  return (
    <Box w="full" onClick={(e) => e.stopPropagation()}>
      {displayedIntents.map((intent, index) => (
        <CampaignIntentItem
          key={intent.id}
          intent={intent}
          index={index}
          campaign={campaign}
          isApplying={applyingId === intent.id}
          isIgnoring={ignoringId === intent.id}
          onApply={handleApply}
          onDismiss={handleDismiss}
          onApplyButtonHover={onApplyButtonHover}
          onApplyButtonLeave={onApplyButtonLeave}
          onScrollToAdset={onScrollToAdset}
        />
      ))}
    </Box>
  );
};

export default CampaignIntentsBlock;
